import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  concatMap,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';

import * as fromGenerated from '../../_generated';
// Concrete file, not the chats barrel: it would pull the chats tree into
// this admin effects file and close an import cycle.
import {
  defaultChatLockKey,
  readApiErrorName,
} from '../../chats/assistants/default-chat-lock.helper';
import { AssistantActions } from '../../chats/assistants/store/assistants.actions';
import { sentryCaptureError } from '../../sentry.helper';
import { AdminManagedSkillsActions } from './admin-managed-skills.actions';
import { adminManagedSkillsFeature } from './admin-managed-skills.reducer';

/**
 * Effects for the Expertises list: the list, the lazy dropdown payload and
 * Supprimer and the default chat designation, all from `/v1/admin/managed-skills` (super_admin at class
 * level).
 */
@Injectable()
export class AdminManagedSkillsEffects {
  private readonly actions$ = inject(Actions);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);

  loadManagedSkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.loadManagedSkills),
      switchMap(() =>
        forkJoin([
          this.adminService.adminManagedSkillsControllerListV1(
            undefined,
            undefined,
            undefined,
            this.currentLocale(),
          ),
          timer(1000),
        ]).pipe(
          map(([skills]) =>
            AdminManagedSkillsActions.loadManagedSkillsSuccess({ skills }),
          ),
          catchError((error: Error) =>
            of(
              AdminManagedSkillsActions.loadManagedSkillsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadManagedSkillsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminManagedSkillsActions.loadManagedSkillsFailure),
        tap(() => this.toastError('ADMIN.MANAGED_SKILLS.TOAST.LOAD_ERROR')),
      ),
    { dispatch: false },
  );

  loadDropdownPayload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.loadDropdownPayload),
      // Read when the request is asked for. mergeMap, as admin-users does,
      // so one slow row never holds the others; the generation still drops
      // a response from before a reload, whatever order they land in.
      withLatestFrom(
        this.store.select(adminManagedSkillsFeature.selectPayloadGeneration),
      ),
      mergeMap(([{ skillId }, generation]) =>
        this.adminService
          .adminManagedSkillsControllerGetDropdownPayloadV1(
            skillId,
            this.currentLocale(),
          )
          .pipe(
            map((payload) =>
              AdminManagedSkillsActions.loadDropdownPayloadSuccess({
                skillId,
                payload,
                generation,
              }),
            ),
            catchError((error: Error) => {
              const failure =
                AdminManagedSkillsActions.loadDropdownPayloadFailure({
                  skillId,
                  error: error.message,
                  generation,
                });
              // A 404 means the skill left the list after it loaded (archived,
              // or no longer managed). Retrying cannot help, so the list
              // reloads and the row goes.
              return error instanceof HttpErrorResponse && error.status === 404
                ? of(failure, AdminManagedSkillsActions.loadManagedSkills())
                : of(failure);
            }),
          ),
      ),
    ),
  );

  // No toast for a dropdown failure: the menu says so itself, in the row
  // the page shows for `dropdownErrors`, and a toast repeated on every
  // reopen was noise.

  // mergeMap, not switchMap: a second row deleted before the first answers
  // must not cancel the first request, since its row stays marked in flight.
  deleteManagedSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.deleteManagedSkill),
      mergeMap(({ skillId, assistant }) =>
        this.adminService.adminManagedSkillsControllerDeleteV1(skillId).pipe(
          mergeMap(() => [
            AdminManagedSkillsActions.deleteManagedSkillSuccess({ skillId }),
            // The owner flow's success, so every listener of a skill delete
            // (the store removal, the chats removal, `skill_deleted`, the
            // success toast) also runs for this one. Needs `AssistantsEffects` registered on
            // this route.
            AssistantActions.deleteAssistantSuccess({
              uuid: skillId,
              assistant,
            }),
          ]),
          sentryCaptureError,
          catchError((error: Error) =>
            of(
              AdminManagedSkillsActions.deleteManagedSkillFailure({
                skillId,
                error: error.message,
                errorName: readApiErrorName(error) ?? null,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // No success toast here: the owner success dispatched alongside it is
  // toasted by `AssistantsEffects.deleteAssistantSuccess$`, the one toast
  // for every skill delete (this page, the builder, My Skills).

  // Every failure the delete route can answer (404: another super_admin
  // already deleted it, or it left the bound; 5xx) leaves a row that may be
  // stale, so the list reloads instead of leaving it as shown.
  deleteManagedSkillFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.deleteManagedSkillFailure),
      tap(({ errorName }) =>
        this.toastActionError(
          defaultChatLockKey(errorName) ??
            'ADMIN.MANAGED_SKILLS.TOAST.DELETE_ERROR',
        ),
      ),
      map(() => AdminManagedSkillsActions.loadManagedSkills()),
    ),
  );

  // concatMap, not mergeMap or switchMap: a designation is never cancelled
  // after the server may have committed it, and two never run at once.
  setDefaultChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.setDefaultChat),
      concatMap(({ skillId }) =>
        this.adminService
          .adminManagedSkillsControllerSetDefaultChatV1(
            skillId,
            this.currentLocale(),
          )
          .pipe(
            map((skill) =>
              AdminManagedSkillsActions.setDefaultChatSuccess({ skill }),
            ),
            sentryCaptureError,
            catchError((error: Error) =>
              of(
                AdminManagedSkillsActions.setDefaultChatFailure({
                  skillId,
                  error: error.message,
                  conflict:
                    error instanceof HttpErrorResponse && error.status === 409,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // The list reloads after a designation: a reload already in flight (started
  // while the confirmation modal was open) would otherwise land after this
  // success and overwrite the new default flags with stale ones. The newer
  // reload wins because loadManagedSkills$ is a switchMap.
  setDefaultChatSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.setDefaultChatSuccess),
      tap(() =>
        this.toastSuccess('ADMIN.MANAGED_SKILLS.TOAST.DEFAULT_CHAT_SUCCESS'),
      ),
      map(() => AdminManagedSkillsActions.loadManagedSkills()),
    ),
  );

  // A 409 means another super_admin designated meanwhile; any other failure
  // leaves the flags uncertain. Either way the list reloads, as a failed
  // Supprimer does.
  setDefaultChatFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminManagedSkillsActions.setDefaultChatFailure),
      tap(({ conflict }) =>
        this.toastError(
          conflict
            ? 'ADMIN.MANAGED_SKILLS.TOAST.DEFAULT_CHAT_CONFLICT'
            : 'ADMIN.MANAGED_SKILLS.TOAST.DEFAULT_CHAT_ERROR',
        ),
      ),
      map(() => AdminManagedSkillsActions.loadManagedSkills()),
    ),
  );

  /**
   * Admin reads resolve localized teammate/skill names server-side, so every
   * request that accepts a locale gets the viewer's current UI language.
   */
  private currentLocale(): 'en' | 'fr' {
    // The app registers only en + fr; narrow at runtime (default en) rather than
    // casting, so an unexpected lang can't reach the API as an invalid locale.
    return this.translateService.getCurrentLang() === 'fr' ? 'fr' : 'en';
  }

  private toastSuccess(key: string): void {
    this.toastrService.success(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }

  private toastError(key: string): void {
    this.toastrService.error(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }

  // A failed delete stays until it is closed, as every skill create, update
  // and delete error does.
  private toastActionError(key: string): void {
    this.toastrService.error(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true, disableTimeOut: true },
    );
  }
}
